import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface PageTitleApi {
  title: string;
  setTitle: (title: string) => void;
}

const PageTitleContext = createContext<PageTitleApi | null>(null);

/**
 * The title shown in the centre of the top bar, published by whichever page is on screen: the page owns its
 * own name, but it is drawn in the header the way the other Kersia applications do it.
 */
export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState('');
  const api = useMemo(() => ({ title, setTitle }), [title]);

  return <PageTitleContext.Provider value={api}>{children}</PageTitleContext.Provider>;
}

function usePageTitleContext(): PageTitleApi {
  const api = useContext(PageTitleContext);
  if (api === null) {
    throw new Error('The page title hooks must be used inside a <PageTitleProvider>.');
  }

  return api;
}

export function usePageTitle(): string {
  return usePageTitleContext().title;
}

export function useSetPageTitle(title: string): void {
  const { setTitle } = usePageTitleContext();

  useEffect(() => {
    setTitle(title);
    return () => setTitle('');
  }, [title, setTitle]);
}
